import { BasePresenter } from "core/presenter";
import { NewSaleReportRepository } from "core/repository";
import Moment from "moment";
import * as FileSaver from "file-saver";
import { fn } from "util";
import { DocumentTypes } from "core/enum";
import { Sale } from "core/entity";

export default class SalesReportPresenter extends BasePresenter {
  initialState() {
    const urlParams = new URLSearchParams(window.location.hash);
    const startDate = Moment(urlParams.get("inicio"), "DD-MM-YYYY");
    const endDate = Moment(urlParams.get("fim"), "DD-MM-YYYY");
    return {
      loading: true,
      filters: {
        dateFrom: startDate.isValid()
          ? startDate
          : Moment()
              .startOf("day")
              .subtract(7, "days"),
        dateTo: endDate.isValid() ? endDate : Moment().endOf("day"),
        reportType: "all",
        saleStatus: "active",
        documentNumber: "",
        documentSerie: "",
        orderType: ""
      },
      pagination: {
        page: 1,
        perPage: 25,
        total: 0
      },
      order: {
        field: "sale.activated_at",
        direction: "DESC"
      },
      results: [],
      summary: {
        total_revenues: 0,
        total_costs: 0,
        profit: 0
      }
    };
  }

  async onStart() {
    this.load();
  }

  msgError(content) {
    this.sendNotification({
      type: "error",
      title: "Oops",
      content: content
    });
  }

  makePagination(perPage, total) {
    this.viewModel.pagination = {
      ...this.viewModel.pagination,
      total: total,
      perPage: perPage
    };
  }

  async load() {
    this.viewModel.loading = true;

    await Promise.all([this.loadResults(), this.loadSummary()]);

    this.viewModel.loading = false;
  }

  async loadResults() {
    this.renewCancelToken();

    const filters = await this.getReportFilters();
    const repositoryResult = await NewSaleReportRepository.getReportTotalSalesTable(
      filters
    );
    if (repositoryResult.success) {
      this.viewModel.results = repositoryResult.data.sales;
      const perPage = repositoryResult.data.pagination.per_page;
      const total = repositoryResult.data.pagination.total_items;
      this.makePagination(perPage, total);
    } else if (!repositoryResult.userCancelled) {
      this.msgError(
        "Não foi possível carregar as vendas.\nTente novamente mais tarde ou entre em contato com o suporte."
      );
    }
  }

  async loadResultsDownload() {
    this.renewCancelToken();
    const filters = await this.getReportFilters();
    filters.count = 99999;
    const repositoryResult = await NewSaleReportRepository.getReportTotalSalesTable(
      filters
    );
    if (repositoryResult.success) {
      return repositoryResult.data.sales;
    }
  }

  async loadSummary() {
    this.renewCancelToken();
    const filters = await this.getReportFilters();
    const repositoryResult = await NewSaleReportRepository.getTotalSalesSummary(
      filters
    );

    if (repositoryResult.success) {
      this.viewModel.summary = repositoryResult.data;
    }
  }

  async getReportFilters() {
    const { filters, pagination } = this.viewModel;
    const {
      dateFrom,
      dateTo,
      documentNumber,
      saleStatus,
      documentSerie,
      reportType,
      orderType
    } = filters;
    const currentUser = await this.mRuntime.get("currentUser");
    const { company_uuid } = currentUser;
    const order = this.getOrder();
    const reportFilters = {
      company_uuid: company_uuid,
      from: Moment(dateFrom)
        .startOf("day")
        .toISOString(),
      to: Moment(dateTo)
        .endOf("day")
        .toISOString(),
      document_number: documentNumber || null,
      document_serie: documentSerie || null,
      order_origin: orderType || null,
      status: saleStatus,
      page: pagination.page,
      order: order.direction === "DESC" ? `-${order.field}` : order.field,
      type: reportType
    };

    return reportFilters;
  }

  async onDownload(format) {
    this.viewModel.loading = true;
    if (format === "csv") {
      await this._downloadCSV();
    } else {
      this.msgError("Ainda não suportamos esse formato");
    }
    this.viewModel.loading = false;
  }

  formatDocumentType(type) {
    return DocumentTypes[type];
  }

  async _downloadCSV() {
    const sales = await this.loadResultsDownload();
    const content = [
      "Data; Tipo; Numero Do Documento; Serie; Status; Faturamento Total; Custos; Lucro"
    ];
    sales.forEach(item => {
      item = new Sale(item);
      const revenuesValue = fn.centsToReal(item.revenues_value);
      const costValue = fn.centsToReal(item.costs_value);
      const profitValue = fn.centsToReal(item.profit);
      const date = Moment(item.activated_at).format("DD/MM/YYYY HH:mm");
      content.push(
        `${date}; ${this.formatDocumentType(item.type)}; ${item.number}; ${
          item.serie
        }; ${item.statusLabel()};${revenuesValue};${costValue};${profitValue}`
      );
    });

    const contentBlob = new Blob([content.join("\n")], {
      type: "application/csv;charset=utf-8"
    });
    FileSaver.saveAs(contentBlob, "relatorio-de-vendas.csv");
  }

  getOrder() {
    const { order } = this.viewModel;

    if (order.field === "document_number") {
      return { direction: order.direction, field: "number" };
    } else if (order.field === "sale.activated_at") {
      return { direction: order.direction, field: "activated_at" };
    }

    return order;
  }

  onFilterChange(filters) {
    if (filters.dateFrom && filters.dateTo) {
      window.location.hash = `?&inicio=${Moment(filters.dateFrom).format(
        "DD-MM-YYYY"
      )}&fim=${Moment(filters.dateTo).format("DD-MM-YYYY")}`;
    }
    for (const key in filters) {
      if (filters[key] !== this.viewModel.filters[key]) {
        this.viewModel.filters = {
          ...this.viewModel.filters,
          ...filters
        };
        this.viewModel.pagination.page = 1;

        this.load();
        break;
      }
    }
  }

  onPageChange(page) {
    this.viewModel.pagination.page = page;
    this.load();
  }

  onOrderChange(field, direction) {
    this.viewModel.order = { field, direction };
    this.load();
  }
}



// WEBPACK FOOTER //
// ./src/components/app/management/sales-report/presenter.js